import { isSelectOptionDisabled } from "./isSelectOptionDisabled";

/**
 * Serializes a select element to an HTML select element
 * using a pre-built flat options map from makeOptionsMap.
 *
 * When `excludeOptions` is true, the returned <select> omits its <option>
 * children entirely and gains an `aria-description` directing Operator to
 * the `lookup_options` tool. Use this for high-cardinality selects
 * (e.g. tag/category lists with thousands of entries) to keep the Operator
 * `data-operator-description` payload small.
 *
 * Disabled options (`isDisabled`) are omitted from `<option>` children — the shared
 * select-tool schema otherwise treats those values as valid targets. They are
 * summarized on `data-unavailable-options` instead.
 */
export const serializeSelect = <OptionType>(
  id: string,
  operatorLabel: string,
  optionsMap: Record<string, OptionType>,
  formatOptionStringLabel: (option: OptionType) => string,
  isDisabled: boolean,
  isMulti = false,
  selectedValues: OptionType[] = [],
  excludeOptions = false
): HTMLSelectElement => {
  const select = document.createElement("select");
  select.id = id;
  select.setAttribute("aria-label", operatorLabel);
  if (isDisabled) {
    select.disabled = true;
  }
  if (isMulti) {
    select.multiple = true;
  }
  if (excludeOptions) {
    select.setAttribute("aria-description", `Search using the lookup_options tool to see available options`);
  }

  // Filter out null/undefined values before mapping to prevent errors
  // when formatOptionStringLabel is called on non-existent values
  const validSelectedValues = selectedValues.filter((v): v is OptionType => v != null);
  const selectedValueStrings = validSelectedValues.map((v) => formatOptionStringLabel(v));
  select.setAttribute("value", selectedValueStrings.join(", "));

  if (excludeOptions) {
    return select;
  }

  const unavailableOptions = Object.entries(optionsMap).filter(([, option]) => isSelectOptionDisabled(option));
  if (unavailableOptions.length > 0) {
    select.setAttribute(
      "data-unavailable-options",
      unavailableOptions.map(([, option]) => `${formatOptionStringLabel(option)} (disabled)`).join(", ")
    );
  }

  Object.entries(optionsMap).forEach(([value, option]) => {
    if (isSelectOptionDisabled(option)) {
      return;
    }
    const optionElement = document.createElement("option");
    optionElement.value = value;
    optionElement.textContent = formatOptionStringLabel(option);
    if (selectedValueStrings.includes(optionElement.textContent || "")) {
      optionElement.setAttribute("selected", "");
    }
    select.appendChild(optionElement);
  });

  return select;
};
